import { useState, useEffect } from 'react'
import { X, Edit, Loader2, AlertCircle } from 'lucide-react'
import { Card } from '../../../../../../components/ui/Card'

/**
 * Modal para editar consumos
 */
export function EditConsumptionModal({ 
  consumption, 
  selectedOrder, 
  recipe,
  onClose, 
  onSubmit,
  onError,
  isSubmitting 
}) {
  if (!consumption) return null
  
  const [formData, setFormData] = useState({
    ComponentSapCode: '',
    DosingUnit: '',
    DosingHopper: ''
  })
  const [validationError, setValidationError] = useState(null)
  const recipeComponents = Array.isArray(recipe?.Components) ? recipe.Components : []

  // Inicializar formulario con datos del consumo
  useEffect(() => {
    if (consumption) {
      setFormData({
        ComponentSapCode: consumption.ComponentSapCode || consumption.componentSapCode || '',
        DosingUnit: consumption.DosingUnit || consumption.dosingUnit || '',
        DosingHopper: consumption.DosingHopper || consumption.hopper || consumption.Hopper || ''
      })
    }
  }, [consumption])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setValidationError(null)
  }

  const handleSubmit = async (e) => {
    e?.preventDefault()
    e?.stopPropagation()
    
    console.log('🔵 [EditConsumptionModal] handleSubmit llamado', { formData, consumption })
    
    // Validación básica
    if (!formData.ComponentSapCode.trim()) {
      console.warn('⚠️ [EditConsumptionModal] Validación fallida - campos vacíos')
      return
    }
    
    setValidationError(null)
    // Llamar a onSubmit con los datos del formulario y el consumo original
    if (onSubmit) {
      console.log('✅ [EditConsumptionModal] Llamando onSubmit con:', { formData, consumption })
      try {
        await onSubmit(formData, consumption)
      } catch (error) {
        console.error('❌ [EditConsumptionModal] Error en onSubmit:', error)
        const data = error?.response?.data
        const ve = Array.isArray(data?.validationErrors) ? data.validationErrors.join('; ') : ''
        const apiDetail = data?.message && ve ? `${data.message}: ${ve}` : ''
        const msg =
          error?.message ||
          apiDetail ||
          data?.message ||
          'Error al guardar'
        setValidationError(msg)
      }
    } else {
      console.error('❌ [EditConsumptionModal] onSubmit no está definido')
    }
  }

  const handleClose = () => {
    setFormData({
      ComponentSapCode: '',
      DosingUnit: '',
      DosingHopper: ''
    })
    setValidationError(null)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Editar Declaración de Consumo
            </h3>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Cerrar modal"
              disabled={isSubmitting}
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="space-y-4">
            {validationError && (
              <div className="bg-red-900/30 border border-red-500 rounded-lg p-3 flex items-start gap-2" role="alert">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-red-400 text-sm">{validationError}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Component <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.ComponentSapCode ?? ''}
                onChange={(e) => handleChange('ComponentSapCode', e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono focus:outline-none focus:border-blue-500"
                disabled={isSubmitting}
              >
                <option value="">Select a component from the recipe</option>
                {recipeComponents.map((component, idx) => {
                  const sapCode = component?.SapCode || component?.sapCode || ''
                  const description = component?.Description || component?.description || ''
                  return (
                    <option key={`${sapCode || 'component'}-${idx}`} value={sapCode}>
                      {sapCode}{description ? ` - ${description}` : ''}
                    </option>
                  )
                })}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                El componente debe pertenecer a la receta de la orden
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Dosificador / Tolva
              </label>
              <div className="w-full px-3 py-2 bg-gray-800/70  text-gray-200">
                {formData.DosingUnit || '-'} / {formData.DosingHopper || '-'}
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={handleSubmit}
                disabled={
                  isSubmitting || 
                  !formData.ComponentSapCode.trim()
                }
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-white">Guardando...</span>
                  </>
                ) : (
                  <>
                    <Edit className="w-4 h-4" />
                    <span className="text-white">Guardar Cambios</span>
                  </>
                )}
              </button>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed rounded-lg transition-colors text-white"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
